import React, { useState, useEffect } from 'react';
import { View, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { createStackNavigator, CardStyleInterpolators } from '@react-navigation/stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { RootStackParamList } from '../types/navigation';
import { useAuth } from '../context/AuthContext';
import { useTutorial, TUTORIAL_COMPLETED_KEY } from '../context/TutorialContext';
import AuthNavigator from './AuthNavigator';
import BottomTabNavigator from '../components/layout/BottomTabNavigator';
import ProfileScreen from '../screens/ProfileScreen';
import PostDetailScreen from '../screens/PostDetailScreen';
import ChatsScreen from '../screens/ChatsScreen';
import PersonalChatScreen from '../screens/PersonalChatScreen';
import SettingsScreen from '../screens/SettingsScreen';
import NotificationScreen from '../screens/NotificationScreen';
import PrivacyPolicyScreen from '../screens/PrivacyPolicyScreen';
import TermsOfUseScreen from '../screens/TermsOfUseScreen';
import SubmitReviewScreen from '../screens/profile/SubmitReviewScreen';
import AdminNotificationScreen from '../screens/AdminNotificationScreen';
import MyProductsScreen from '../screens/MyProductsScreen';
import SocialLinksScreen from '../screens/SocialLinksScreen';
import MyPaymentsScreen from '../screens/clubs/MyPaymentsScreen';
import AdminPaymentsScreen from '../screens/admin/AdminPaymentsScreen';
import DeactivatedScreen from '../screens/DeactivatedScreen';
import FindFriendsScreen from '../screens/auth/FindFriendsScreen';
import FeedPostSkeleton from '../components/common/FeedPostSkeleton';
import Skeleton from '../components/common/Skeleton';
import { useTheme } from '../theme/ThemeContext';

const Stack = createStackNavigator<RootStackParamList>();

const FIND_FRIENDS_COMPLETED_KEY = '@lnb_find_friends_completed';

const RootNavigator: React.FC = () => {
  const { isLoading, isAuthenticated, user } = useAuth();
  const { startTutorial } = useTutorial();
  const { theme } = useTheme();
  const [findFriendsCompleted, setFindFriendsCompleted] = useState<boolean | null>(null);
  const [hasCheckedTutorial, setHasCheckedTutorial] = useState(false);

  const isDeactivated = Boolean(isAuthenticated && user && user.role === 'deactivated');
  const showAuthFlow = !isAuthenticated || !user || !user.onboardingCompleted;
  // Disable Find Friends on iOS to avoid permission issues during App Store review
  const shouldCheckFindFriends = !showAuthFlow && !isDeactivated && Platform.OS !== 'web' && Platform.OS !== 'ios';

  // Check if tutorial should start when user reaches main app for first time
  useEffect(() => {
    const checkAndStartTutorial = async () => {
      if (hasCheckedTutorial) return;
      
      // Only show tutorial when user is authenticated, onboarding is complete, and we're on main tabs
      if (!showAuthFlow && !isDeactivated && isAuthenticated && user && user.onboardingCompleted) {
        const tutorialCompleted = await AsyncStorage.getItem(TUTORIAL_COMPLETED_KEY);
        const findFriendsComplete = await AsyncStorage.getItem(FIND_FRIENDS_COMPLETED_KEY);
        
        // Start tutorial if it hasn't been completed and FindFriends has been shown/skipped
        if (!tutorialCompleted && findFriendsComplete) {
          console.log('📚 Starting first-time tutorial after onboarding');
          setTimeout(() => {
            startTutorial();
          }, 500); // Small delay to let navigation settle
        }
        
        setHasCheckedTutorial(true);
      }
    };

    checkAndStartTutorial();
  }, [showAuthFlow, isDeactivated, isAuthenticated, user, hasCheckedTutorial, startTutorial]);

  useEffect(() => {
    const checkFindFriendsStatus = async () => {
      // On web and iOS, automatically mark as completed to skip the screen
      if (Platform.OS === 'web' || Platform.OS === 'ios') {
        console.log(`🌐 ${Platform.OS} platform - skipping FindFriends screen`);
        // Persist the completion state so tutorial can start
        await AsyncStorage.setItem(FIND_FRIENDS_COMPLETED_KEY, 'true');
        setFindFriendsCompleted(true);
        return;
      }
      
      try {
        const completed = await AsyncStorage.getItem(FIND_FRIENDS_COMPLETED_KEY);
        setFindFriendsCompleted(completed === 'true');
      } catch (error) {
        console.error('Error checking FindFriends status:', error);
        setFindFriendsCompleted(false);
      }
    };

    if (shouldCheckFindFriends) {
      checkFindFriendsStatus();
    } else {
      // For iOS/web, still run the check to mark as completed
      if (Platform.OS === 'ios' || Platform.OS === 'web') {
        checkFindFriendsStatus();
      } else {
        setFindFriendsCompleted(null);
      }
    }
  }, [shouldCheckFindFriends, isAuthenticated, user]);

  if (isLoading || (shouldCheckFindFriends && findFriendsCompleted === null)) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: theme.background }] }>
        <View style={styles.loadingContent}>
          <Skeleton width="45%" height={28} borderRadius={14} />
          <FeedPostSkeleton />
          <FeedPostSkeleton />
        </View>
        <ActivityIndicator size="large" color={theme.primary} style={styles.loadingSpinner} />
      </View>
    );
  }

  const showFindFriends = shouldCheckFindFriends && findFriendsCompleted === false;

  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: false,
        cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
        transitionSpec: {
          open: {
            animation: 'timing',
            config: {
              duration: 250,
            },
          },
          close: {
            animation: 'timing',
            config: {
              duration: 200,
            },
          },
        },
      }}
    >
      {showAuthFlow ? (
        <Stack.Screen name="Auth" component={AuthNavigator} />
      ) : showFindFriends ? (
        <Stack.Screen
          name="FindFriends"
          component={FindFriendsScreen}
          options={{
            headerShown: false,
            gestureEnabled: false,
          }}
        />
      ) : isDeactivated ? (
        <Stack.Screen
          name="Deactivated"
          component={DeactivatedScreen}
          options={{
            headerShown: false,
            gestureEnabled: false,
          }}
        />
      ) : (
        <>
          <Stack.Screen name="MainTabs" component={BottomTabNavigator} />
          <Stack.Screen
            name="Profile"
            component={ProfileScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
              keyboardHandlingEnabled: false,
            }}
          />
          <Stack.Screen
            name="PostDetail"
            component={PostDetailScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="Chats"
            component={ChatsScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="PersonalChat"
            component={PersonalChatScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="Settings"
            component={SettingsScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="Notification"
            component={NotificationScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="PrivacyPolicy"
            component={PrivacyPolicyScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="TermsOfUse"
            component={TermsOfUseScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="SubmitReview"
            component={SubmitReviewScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="AdminNotification"
            component={AdminNotificationScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="MyProducts"
            component={MyProductsScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="SocialLinks"
            component={SocialLinksScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="MyPayments"
            component={MyPaymentsScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
          <Stack.Screen
            name="AdminPayments"
            component={AdminPaymentsScreen}
            options={{
              presentation: 'card',
              cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
            }}
          />
        </>
      )}
    </Stack.Navigator>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  loadingContent: {
    width: '100%',
    maxWidth: 420,
  },
  loadingSpinner: {
    position: 'absolute',
    bottom: 48,
  },
});

export default RootNavigator;
